import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Org } from '@vidmakr/bindings/types';
import { LIST_PRICES, ADDON_LIST_PRICES, isEditorRole, orgLimits, usagePeriod, type BillingInterval, type OrgLimits } from '@vidmakr/shared';
import { api } from './api';
import { errorMessage, toast } from './toast';
import { useOrg } from './hooks';

export interface Prices {
  pro: Record<BillingInterval, number>;
  team: Record<BillingInterval, number>;
  enterprise: Record<BillingInterval, number>;
  storage: number;
  renderPack: number;
  currency: string;
}

export interface BillingInfo {
  /** Plans and allowances apply on this installation. */
  enabled: boolean;
  /** Stripe Checkout is available. */
  checkout: boolean;
  prices: Prices;
}

const FALLBACK: BillingInfo = {
  enabled: false,
  checkout: false,
  prices: { pro: LIST_PRICES.pro, team: LIST_PRICES.team, enterprise: LIST_PRICES.enterprise, storage: ADDON_LIST_PRICES.storagePerMonth, renderPack: ADDON_LIST_PRICES.renderPack, currency: 'usd' },
};

let infoPromise: Promise<BillingInfo> | null = null;
let infoValue: BillingInfo | null = null;

/** Whether billing is on, and current prices (fetched once per page load). */
export function useBillingInfo(): BillingInfo | null {
  const [info, setInfo] = useState(infoValue);
  useEffect(() => {
    if (infoValue) return;
    infoPromise ??= api<BillingInfo>('/api/billing/plans')
      .then((v) => (infoValue = v))
      .catch(() => (infoValue = FALLBACK));
    void infoPromise.then(setInfo);
  }, []);
  return info;
}

const nowMicros = () => BigInt(Date.now()) * 1000n;

export interface OrgPlan {
  org: Org;
  limits: OrgLimits;
  enabled: boolean;
  usage: { renderMinutes: number; aiMinutes: number; bonusRenderMinutes: number; exports: number };
  renderAllowance: number;
  renderLeft: number;
  aiLeft: number;
  editors: number;
  viewers: number;
  /** Whole days left in a trial (null when not on one). */
  trialDaysLeft: number | null;
}

/** An organization's plan, limits and this month's usage. */
export function useOrgPlan(orgId: string | undefined): OrgPlan | null {
  const info = useBillingInfo();
  const { org, members } = useOrg(orgId);
  const [usageRows] = useTable(tables.orgUsage);
  const period = usagePeriod(nowMicros());
  return useMemo(() => {
    if (!org || !info) return null;
    const enabled = info.enabled;
    const limits = orgLimits(org, nowMicros(), { enabled });
    const u = usageRows.find((r) => r.orgId === org.id && r.period === period);
    const usage = { renderMinutes: u?.renderMinutes ?? 0, aiMinutes: u?.aiMinutes ?? 0, bonusRenderMinutes: u?.bonusRenderMinutes ?? 0, exports: u?.exports ?? 0 };
    const renderAllowance = limits.renderMinutes + usage.bonusRenderMinutes;
    const editors = members.filter((m) => isEditorRole(m.role)).length;
    const trialDaysLeft =
      enabled && limits.plan.id === 'trial' && org.trialEndsAt ? Math.max(0, Math.ceil(Number(org.trialEndsAt.microsSinceUnixEpoch - nowMicros()) / 86_400e6)) : null;
    return {
      org,
      limits,
      enabled,
      usage,
      renderAllowance,
      renderLeft: Math.max(0, renderAllowance - usage.renderMinutes),
      aiLeft: Math.max(0, limits.aiMinutes - usage.aiMinutes),
      editors,
      viewers: members.length - editors,
      trialDaysLeft,
    };
  }, [org, info, usageRows, period, members]);
}

/** Calls a billing endpoint that answers with a Stripe URL, and goes there. */
export async function goToStripe(path: string, body: object) {
  try {
    const { url } = await api<{ url: string }>(path, { method: 'POST', json: body });
    window.location.href = url;
  } catch (e) {
    toast.error(errorMessage(e));
  }
}

export const formatMoney = (amount: number, currency = 'usd') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);

export const formatMinutes = (n: number) => (Number.isFinite(n) ? (n >= 100 ? Math.round(n).toLocaleString() : String(Math.round(n * 10) / 10)) : 'Unlimited');
